import React, { useMemo, useState } from 'react';
import { Button, Badge, Checkbox, Tag, Tooltip, Avatar, Space, Dropdown, Typography, message, Popconfirm } from 'antd';
import { ChevronDown, ChevronRight, Clock, Filter, MenuIcon, MoreVertical, SortAsc, SortDesc, Trash2, Edit } from 'lucide-react';
import KanbanTaskStore, { KanbanTask } from '../../stores/KanbanTaskStores';
import { getPriorityColor, getPriorityIcon, getPriorityText, formatDate, getTimeRemaining } from './useTaskHelpers';
import dayjs from 'dayjs';
import { useStore } from '../../stores/root-store-context';
import { ka } from 'date-fns/locale';

const { Text } = Typography;

interface ListViewProps {
  tasks: KanbanTask[];
  statuses: any[];
  users: any[];
  onTaskClick: (task: KanbanTask) => void;
  t: (key: string) => string;
  showDeletedTasks: boolean;
  setShowDeletedTasks: (value: boolean) => void;
}

const ListView: React.FC<ListViewProps> = ({ tasks, statuses, users, onTaskClick, t, showDeletedTasks, setShowDeletedTasks }) => {
  const [sortField, setSortField] = useState<string>('priority');
  const [sortDirection, setSortDirection] = useState<'ascend' | 'descend'>('descend');

  const [expandedGroups, setExpandedGroups] = useState<string[]>(statuses.map(status => status.id));

  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);

  const [hideCompletedTasks, setHideCompletedTasks] = useState<boolean>(false);
  const [showOverdueTasks, setShowOverdueTasks] = useState<boolean>(false);
  const { applicationStore, kanbanTaskStore } = useStore();

  const tasksByStatus = useMemo(() => {
    let filteredTasks = [...tasks];

    if (hideCompletedTasks) {
      const completedStatuses = statuses
        .filter(status => status.name.toLowerCase().includes('closed') ||
          status.name.toLowerCase().includes('completed') ||
          status.name.toLowerCase().includes('done'))
        .map(status => status.id);

      filteredTasks = filteredTasks.filter(task => !completedStatuses.includes(task.statusId));
    }

    if (showOverdueTasks) {
      filteredTasks = filteredTasks.filter(task => {
        if (!task.dueDate) return false;
        return dayjs(task.dueDate).isBefore(dayjs(), 'day');
      });
    }

    return statuses.map(status => ({
      status,
      tasks: filteredTasks
        .filter(task => task.statusId === status.id)
        .sort((a, b) => {
          if (sortField === 'title') {
            return sortDirection === 'ascend'
              ? a.title.localeCompare(b.title)
              : b.title.localeCompare(a.title);
          }

          if (sortField === 'priority') {
            return sortDirection === 'ascend'
              ? a.priority - b.priority
              : b.priority - a.priority;
          }

          if (sortField === 'dueDate') {
            const aDate = a.dueDate ? new Date(a.dueDate?.toString()).getTime() : 0;
            const bDate = b.dueDate ? new Date(b.dueDate?.toString()).getTime() : 0;
            return sortDirection === 'ascend' ? aDate - bDate : bDate - aDate;
          }

          if (sortField === 'startDate') {
            const aDate = a.startDate ? new Date(a.startDate?.toString()).getTime() : 0;
            const bDate = b.startDate ? new Date(b.startDate?.toString()).getTime() : 0;
            return sortDirection === 'ascend' ? aDate - bDate : bDate - aDate;
          }

          return a.index - b.index;
        })
    }));
  }, [tasks, statuses, sortField, sortDirection, hideCompletedTasks, showOverdueTasks]);

  const toggleGroup = (statusId: string) => {
    if (expandedGroups.includes(statusId)) {
      setExpandedGroups(expandedGroups.filter(id => id !== statusId));
    } else {
      setExpandedGroups([...expandedGroups, statusId]);
    }
  };

  const handleSortChange = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ascend' ? 'descend' : 'ascend');
    } else {
      setSortField(field);
      setSortDirection('descend');
    }
  };

  const handleSelectTask = (taskId: string, checked: boolean) => {
    if (checked) {
      setSelectedTaskIds([...selectedTaskIds, taskId]);
    } else {
      setSelectedTaskIds(selectedTaskIds.filter(id => id !== taskId));
    }
  };

  const handleSelectGroup = (statusId: string, checked: boolean) => {
    const groupTaskIds = tasks
      .filter(task => task.statusId === statusId)
      .map(task => task.id.toString());

    if (checked) {
      const newSelected = [...new Set([...selectedTaskIds, ...groupTaskIds])];
      setSelectedTaskIds(newSelected);
    } else {
      setSelectedTaskIds(selectedTaskIds.filter(id => !groupTaskIds.includes(id)));
    }
  };

  const getAssigneeName = (assigneeId: string | undefined) => {
    if (!assigneeId) return t('Unassigned');
    const user = users.find(u => String(u.id) === String(assigneeId));
    return user ? `${user.firstName} ${user.lastName}` : t('Unassigned');
  };

  const renderTableHeader = () => (
    <div className="list-header">
      <div className="list-header-checkbox"></div>
      <div
        className="list-header-title hover-effect"
        onClick={() => handleSortChange('title')}
      >
        {t('Title')}
        {sortField === 'title' && (
          sortDirection === 'ascend' ? <SortAsc size={14} className="sort-icon" /> : <SortDesc size={14} className="sort-icon" />
        )}
      </div>
      <div
        className="list-header-priority hover-effect"
        onClick={() => handleSortChange('priority')}
      >
        {t('Priority')}
        {sortField === 'priority' && (
          sortDirection === 'ascend' ? <SortAsc size={14} className="sort-icon" /> : <SortDesc size={14} className="sort-icon" />
        )}
      </div>
      <div className="list-header-assignee">{t('Assignee')}</div>
      <div
        className="list-header-start-date hover-effect"
        onClick={() => handleSortChange('startDate')}
      >
        {t('StartDate')}
        {sortField === 'startDate' && (
          sortDirection === 'ascend' ? <SortAsc size={14} className="sort-icon" /> : <SortDesc size={14} className="sort-icon" />
        )}
      </div>
      <div
        className="list-header-due-date hover-effect"
        onClick={() => handleSortChange('dueDate')}
      >
        {t('DueDate')}
        {sortField === 'dueDate' && (
          sortDirection === 'ascend' ? <SortAsc size={14} className="sort-icon" /> : <SortDesc size={14} className="sort-icon" />
        )}
      </div>
      <div className="list-header-status">{t('Status')}</div>
      <div className="list-header-actions"></div>
    </div>
  );

  const deleteSelectedTasks = async () => {
    try {
      if (showDeletedTasks)
        await kanbanTaskStore.bulkPermanentDelete(selectedTaskIds);
      else
        await kanbanTaskStore.bulkSoftDelete(selectedTaskIds);

      message.success(
        showDeletedTasks ? t('SelectedTasksDeletedPermanent') : t('SelectedTasksDeleted')
      );

      setSelectedTaskIds([]);

    } catch (err) {
      console.error(err);
      message.error(t('FailedToDeleteSelected'));
    }
  };

  const restoreSelectedTasks = async () => {
    try {
      await kanbanTaskStore.bulkRestore(selectedTaskIds);

      message.success(t('SelectedTasksRestored'));

      setSelectedTaskIds([]);

    } catch (error) {
      console.error(error);
      message.error(t('FailedToRestoreSelected'));
    }
  };

  return (
    <div className="list-view">
      <div className="list-controls">
        <Space>
          <Button
            icon={<Filter size={16} />}
            onClick={() => setHideCompletedTasks(!hideCompletedTasks)}
            type={hideCompletedTasks ? 'primary' : 'default'}
            className="hover-effect"
          >
            {hideCompletedTasks ? t('ShowingActive') : t('ShowAll')}
          </Button>
          <Button
            icon={<Clock size={16} />}
            onClick={() => setShowOverdueTasks(!showOverdueTasks)}
            type={showOverdueTasks ? 'primary' : 'default'}
            className="hover-effect"
          >
            {showOverdueTasks ? t('ShowingOverdue') : t('ShowAllDates')}
          </Button>
          <Button
            type={showDeletedTasks ? 'primary' : 'default'}
            onClick={() => setShowDeletedTasks(!showDeletedTasks)}
            className="hover-effect"
          >
            {showDeletedTasks ? t('ShowAllTasks') : t('ShowDeletedTasks')}
          </Button>
          {showDeletedTasks && selectedTaskIds.length > 0 && (
            <Badge count={selectedTaskIds.length} offset={[5, 0]} style={{ marginRight: 10}}>
              <Popconfirm
                title={t('ConfirmRestore')}
                description={t('AreYouSureRestore')}
                okText={t('Restore')}
                cancelText={t('Cancel')}
                onConfirm={restoreSelectedTasks}
              >
                <Button type="primary" className="hover-effect">
                  {t('RestoreSelected')}
                </Button>
              </Popconfirm>
            </Badge>
          )}
          {selectedTaskIds.length > 0 && (
            <Badge count={selectedTaskIds.length} offset={[5, 0]}>
              <Popconfirm
                title={t('ConfirmDeleteTask')}
                description={
                  showDeletedTasks
                    ? t('AreYouSureDeleteTaskPermanent')
                    : t('AreYouSureDeleteTask')
                }
                okText={t('Delete')}
                okType="danger"
                cancelText={t('Cancel')}
                onConfirm={deleteSelectedTasks}
              >
                <Button danger icon={<Trash2 size={16} />} className="hover-effect">
                  {t('DeleteSelected')}
                </Button>
              </Popconfirm>
            </Badge>
          )}
        </Space>

        <Space>
          <Dropdown
            menu={{
              items: [
                { key: 'expandAll', label: t('ExpandAll'), icon: <ChevronDown size={16} /> },
                { key: 'collapseAll', label: t('CollapseAll'), icon: <ChevronRight size={16} /> },
                { type: 'divider' },
                { key: 'sortTitle', label: t('SortByTitle'), icon: <SortAsc size={16} /> },
                { key: 'sortPriority', label: t('SortByPriority'), icon: <SortAsc size={16} /> },
                { key: 'sortDueDate', label: t('SortByDueDate'), icon: <SortAsc size={16} /> }
              ],
              onClick: ({ key }) => {
                if (key === 'expandAll') {
                  setExpandedGroups(statuses.map(s => s.id));
                } else if (key === 'collapseAll') {
                  setExpandedGroups([]);
                } else if (key === 'sortTitle') {
                  setSortField('title');
                  setSortDirection('ascend');
                } else if (key === 'sortPriority') {
                  setSortField('priority');
                  setSortDirection('descend');
                } else if (key === 'sortDueDate') {
                  setSortField('dueDate');
                  setSortDirection('ascend');
                }
              }
            }}
          >
            <Button icon={<MenuIcon size={16} />} className="hover-effect">{t('Options')}</Button>
          </Dropdown>
        </Space>
      </div>

      {renderTableHeader()}

      <div className="list-content">
        {tasksByStatus.map(({ status, tasks: statusTasks }) => (
          <div key={status.id} className="status-group">
            <div
              className="status-group-header hover-effect"
              onClick={() => toggleGroup(status.id)}
            >
              <Checkbox
                checked={statusTasks.length > 0 && statusTasks.every(task => selectedTaskIds.includes(task.id.toString()))}
                indeterminate={
                  statusTasks.some(task => selectedTaskIds.includes(task.id.toString())) &&
                  !statusTasks.every(task => selectedTaskIds.includes(task.id.toString()))
                }
                onChange={(e) => handleSelectGroup(status.id, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                className="status-group-checkbox"
              />
              <div className="status-group-title">
                {expandedGroups.includes(status.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span className="status-name">{status.name}</span>
                <Badge count={statusTasks.length} className="status-badge" />
              </div>
            </div>

            {expandedGroups.includes(status.id) && statusTasks.map(task => {
              const timeRemaining = task.dueDate ? getTimeRemaining(task.dueDate?.toString(), t) : null;

              return (
                <div
                  key={task.id}
                  className={`task-row ${selectedTaskIds.includes(task.id.toString()) ? 'selected' : ''} hover-effect`}
                >
                  <Checkbox
                    checked={selectedTaskIds.includes(task.id.toString())}
                    onChange={(e) => handleSelectTask(task.id.toString(), e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    className="task-checkbox"
                  />
                  <div
                    className="task-title"
                    onClick={() => onTaskClick(task)}
                  >
                    <Tooltip title={task.title}>
                      <span>{task.title}</span>
                    </Tooltip>
                  </div>
                  <div className="task-priority">
                    <Tag color={getPriorityColor(task.priority)} className="priority-tag">
                      <div className="priority-tag-content">
                        {getPriorityIcon(task.priority)}
                        <span>{getPriorityText(task.priority, t)}</span>
                      </div>
                    </Tag>
                  </div>
                  <div className="task-assignee">
                    {task.assigneeId ? (
                      <div className="assignee-info">
                        <Avatar
                          size="small"
                          src={task.assignee?.photoId ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${task.assignee.photoId}` : undefined}
                          className="assignee-avatar"
                        >
                          {task.assignee?.firstName?.[0]}
                          {task.assignee?.lastName?.[0]}
                        </Avatar>
                        <span className="assignee-name">
                          {getAssigneeName(task.assigneeId)}
                        </span>
                      </div>
                    ) : (
                      <Text type="secondary" className="unassigned">{t('Unassigned')}</Text>
                    )}
                  </div>
                  <div className="task-start-date">
                    {formatDate(task.startDate?.toString())}
                  </div>
                  <div className="task-due-date">
                    <div className="due-date-container">
                      <span>{formatDate(task.dueDate?.toString())}</span>
                      {timeRemaining && (
                        <Tag color={timeRemaining.color} className="time-remaining-tag">
                          <Clock size={12} className="time-icon" />
                          <span>{timeRemaining.text}</span>
                        </Tag>
                      )}
                    </div>
                  </div>
                  <div className="task-status">
                    <Tag className="status-tag">{status.name}</Tag>
                  </div>
                  <div className="task-actions">
                    <Dropdown
                      menu={{
                        items: [
                          { key: 'edit', label: t('Edit'), icon: <Edit size={14} /> },
                          { key: 'delete', label: t('Delete'), icon: <Trash2 size={14} />, danger: true }
                        ],
                        onClick: ({ key }) => {
                          if (key === 'edit') {
                            onTaskClick(task);
                          } else if (key === 'delete') {
                            // Handle delete
                          }
                        }
                      }}
                      trigger={['click']}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<MoreVertical size={16} />}
                        onClick={(e) => e.stopPropagation()}
                        className="task-action-button hover-effect"
                      />
                    </Dropdown>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ListView;